import { FC, useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subMonths, parseISO } from 'date-fns';
import axios from 'axios';
import { AccountContext, EDITOR_ROLES } from 'auth/accountProvider';
import Popup from 'components/popup/Popup';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import './UploadHistoryTab.scss';

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface UploadHistoryTabProps {
  getSessionTokensFn: () => Promise<SessionTokens>;
}

interface UploadHistoryItem {
  auditId: string;
  filename: string;
  fileType: string;
  uploadedAt: string;
  overallSuccess: boolean;
  totalOrders: number;
  successfulOrders: number;
  failedOrders: number;
  incompleteAddOrders: number;
  orderDateMin: string | null;
  orderDateMax: string | null;
}

const FILE_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'tms', label: 'TMS' },
  { value: 'fuel_consumption', label: 'FUEL' },
  { value: 'hoc', label: 'HOC' },
  { value: 'vehicle_metrics', label: 'Vehicle' },
] as const;

const FILE_TYPE_BADGE_CLASS: Record<string, string> = {
  transport_order: 'upload-history-tab__badge--tms',
  tms_json: 'upload-history-tab__badge--tms',
  fuel_consumption: 'upload-history-tab__badge--fuel',
  hoc: 'upload-history-tab__badge--hoc',
  vehicle_metrics: 'upload-history-tab__badge--vm',
  vehicle: 'upload-history-tab__badge--vm',
  transport_order_add: 'upload-history-tab__badge--add',
};

const FILE_TYPE_DISPLAY: Record<string, string> = {
  transport_order: 'TMS',
  tms_json: 'TMS',
  fuel_consumption: 'FUEL',
  hoc: 'HOC',
  vehicle_metrics: 'Vehicle',
  vehicle: 'Vehicle',
  transport_order_add: 'TRANSPORT ADD',
};

const ITEMS_PER_PAGE = 20;

// transport_order_add files enrich existing rows rather than owning them, so
// the backend has no safe standalone cascade for them.
const NON_DELETABLE_TYPES = ['transport_order_add'];

const formatDate = (date: Date | null): string => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

/* Order date range helper — hidden for now
const formatDisplayDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return format(parseISO(dateStr), 'd MMM yyyy');
};
*/

const getStatusInfo = (item: UploadHistoryItem): { label: string; className: string } => {
  if (item.overallSuccess) {
    return { label: 'Success', className: 'upload-history-tab__status--success' };
  }
  if (item.successfulOrders > 0) {
    return { label: 'Partial', className: 'upload-history-tab__status--partial' };
  }
  return { label: 'Failed', className: 'upload-history-tab__status--failed' };
};

const UploadHistoryTab: FC<UploadHistoryTabProps> = ({ getSessionTokensFn }) => {
  const { t } = useTranslation();
  const { hasRole } = useContext(AccountContext);
  const canDelete = hasRole(EDITOR_ROLES);

  const [items, setItems] = useState<UploadHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(subMonths(new Date(), 6));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  // Committed dates only update on Apply to avoid mid-selection fetches from the range picker
  const [committedStartDate, setCommittedStartDate] = useState<Date | null>(subMonths(new Date(), 6));
  const [committedEndDate, setCommittedEndDate] = useState<Date | null>(new Date());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { jwtToken, sessionId } = await getSessionTokensFn();
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ITEMS_PER_PAGE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedType) params.set('fileType', selectedType);
      if (committedStartDate) params.set('dateFrom', formatDate(committedStartDate));
      if (committedEndDate) params.set('dateTo', formatDate(committedEndDate));

      const res = await axios.get<{ data: UploadHistoryItem[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/upload/history?${params}`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );
      setItems(res.data.data);
      setTotal(res.data.total);
    } catch {
      setError(t('Failed to load upload history. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, selectedType, committedStartDate, committedEndDate, getSessionTokensFn, t]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const [pendingDelete, setPendingDelete] = useState<UploadHistoryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // A notice about the *list* rather than the dialog (e.g. the row was already
  // gone), shown above the table so the refreshed list stays visible.
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);

  // Dismissal (the header close button or Escape, both wired up by Popup's
  // useDialogA11y) must never close the dialog while a delete is in flight —
  // isModalDisabled only disables the footer buttons, not the header close
  // button, so this guard is what actually enforces it.
  const closeDeleteDialog = useCallback(() => {
    if (isDeleting) return;
    setPendingDelete(null);
  }, [isDeleting]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const { jwtToken, sessionId } = await getSessionTokensFn();
      await axios.delete(`${process.env.REACT_APP_LPI_API_URL}/upload/files`, {
        params: { filename: pendingDelete.filename, fileType: pendingDelete.fileType },
        headers: {
          Authorization: `Session ${jwtToken}`,
          'X-Session-Id': sessionId,
        },
      });
      setPendingDelete(null);
      await fetchHistory();
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 404) {
        // Someone else already deleted it, so the row we are looking at is
        // stale. Retrying can only ever 404 again — close the dialog and
        // refetch instead of leaving a phantom row that can never be removed.
        setPendingDelete(null);
        setDeleteNotice(t('This file no longer exists. The list has been refreshed.'));
        await fetchHistory();
        return;
      }
      setDeleteError(t('This file could not be deleted. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return (
    <div className="upload-history-tab">
      <div className="upload-history-tab__header">
        <h1>{t('Upload History')}</h1>
        <p className="upload-history-tab__subtitle">{t('View all files uploaded to the system')}</p>
      </div>

      <div className="upload-history-tab__controls">
        <div className="upload-history-tab__filter-row">
          <DateRangeDropdown
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <button
            type="button"
            className="btn btn-primary upload-history-tab__apply-btn"
            onClick={() => { setCommittedStartDate(startDate); setCommittedEndDate(endDate); setPage(1); }}
            disabled={!startDate || !endDate}
          >
            {t('Apply')}
          </button>
        </div>
      </div>

      <div className="upload-history-tab__card">
        <h2 className="upload-history-tab__card-title">{t('File History')}</h2>
        <div className="upload-history-tab__search-row">
          <input
            type="text"
            className="upload-history-tab__search-input"
            placeholder={t('Search by filename…')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <div className="upload-history-tab__type-chips">
            {FILE_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`upload-history-tab__type-chip ${selectedType === opt.value ? 'upload-history-tab__type-chip--active' : ''}`}
                onClick={() => { setSelectedType(opt.value); setPage(1); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <p className="upload-history-tab__result-count">
          <strong>{total}</strong> {t('uploads matching current filters')}
        </p>

        {error && <div className="upload-history-tab__error">{error}</div>}

        {deleteNotice && (
          <div className="upload-history-tab__notice" role="status">{deleteNotice}</div>
        )}

      {!error && (
        <>
          <div className={`upload-history-tab__table-wrap${isLoading ? ' upload-history-tab__table-wrap--loading' : ''}`}>
            {isLoading && (
              <div className="upload-history-tab__loading-overlay">
                <span className="upload-history-tab__spinner" />
                <span>{t('Loading…')}</span>
              </div>
            )}
            <table className="upload-history-tab__table">
              <thead>
                <tr>
                  <th>{t('Filename')}</th>
                  <th>{t('Type')}</th>
                  <th>{t('Uploaded at')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Row counts')}</th>
                  {/* <th>{t('Order date range')}</th> */}
                  {canDelete && <th className="upload-history-tab__actions-head">{t('Actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {!isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={canDelete ? 6 : 5} className="upload-history-tab__empty">
                      {t('No uploads found for the selected filters.')}
                    </td>
                  </tr>
                )}
                {items.map((item) => {
                  const status = getStatusInfo(item);
                  return (
                    <tr key={item.auditId}>
                      <td>
                        <div className="upload-history-tab__filename" title={item.filename}>
                          {item.filename}
                        </div>
                      </td>
                      <td>
                        <span className={`upload-history-tab__badge ${FILE_TYPE_BADGE_CLASS[item.fileType] ?? ''}`}>
                          {FILE_TYPE_DISPLAY[item.fileType] ?? item.fileType}
                        </span>
                      </td>
                      <td>
                        <div className="upload-history-tab__date-main">
                          {format(parseISO(item.uploadedAt), 'd MMM yyyy')}
                        </div>
                        <div className="upload-history-tab__date-sub">
                          {format(parseISO(item.uploadedAt), 'HH:mm')}
                        </div>
                      </td>
                      <td>
                        <span className={`upload-history-tab__status ${status.className}`}>
                          {t(status.label)}
                        </span>
                      </td>
                      <td className="upload-history-tab__counts">
                        <span className="upload-history-tab__counts-total">
                          {item.totalOrders.toLocaleString()}
                        </span>
                      </td>
                      {/* Order date range — hidden for now
                      <td className="upload-history-tab__range">
                        {item.orderDateMin && item.orderDateMax
                          ? `${formatDisplayDate(item.orderDateMin)} – ${formatDisplayDate(item.orderDateMax)}`
                          : '—'}
                      </td>
                      */}
                      {canDelete && (
                        <td className="upload-history-tab__actions">
                          {!NON_DELETABLE_TYPES.includes(item.fileType) && (
                            <button
                              type="button"
                              className="upload-history-tab__delete-btn"
                              aria-label={t('Delete {{filename}}', { filename: item.filename })}
                              title={t('Delete {{filename}}', { filename: item.filename })}
                              onClick={() => { setDeleteError(null); setDeleteNotice(null); setPendingDelete(item); }}
                            >
                              <svg
                                className="upload-history-tab__delete-icon"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                focusable="false"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482 41.03 41.03 0 00-2.365-.298V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="upload-history-tab__pagination">
            {totalPages > 1 && (
              <>
                <span className="upload-history-tab__page-info">
                  {t('Showing')} {((page - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(page * ITEMS_PER_PAGE, total)} {t('of')} {total}
                </span>
                <button
                  type="button"
                  className="upload-history-tab__page-btn"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ←
                </button>
                {(() => {
                  const windowSize = 5;
                  const halfWindow = Math.floor(windowSize / 2);
                  let startPage = Math.max(1, page - halfWindow);
                  const endPage = Math.min(totalPages, startPage + windowSize - 1);
                  if (endPage - startPage + 1 < windowSize) {
                    startPage = Math.max(1, endPage - windowSize + 1);
                  }
                  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
                  return pageNumbers.map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      className={`upload-history-tab__page-btn ${page === pageNum ? 'upload-history-tab__page-btn--active' : ''}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                <button
                  type="button"
                  className="upload-history-tab__page-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  →
                </button>
              </>
            )}
          </div>
        </>
      )}
      </div>

      {pendingDelete && (
        <Popup
          title={t('Delete this file?')}
          isPopupOpen
          handleClose={closeDeleteDialog}
          cancelAction={closeDeleteDialog}
          handleAction={confirmDelete}
          leftButtonTitle={isDeleting ? t('Deleting…') : t('Confirm delete')}
          rightButtonTitle={t('Cancel')}
          isModalDisabled={isDeleting}
        >
          <div className="upload-history-tab__confirm-body w-100">
            <p>
              {t('All data from {{filename}} will be removed. Reports and dashboards for this tenant will be unavailable while the metrics are rebuilt, and rebuilding runs in the background and can take a while. This cannot be undone.', { filename: pendingDelete.filename })}
            </p>
            {deleteError && <p className="upload-history-tab__confirm-error">{deleteError}</p>}
          </div>
        </Popup>
      )}
    </div>
  );
};

export default UploadHistoryTab;
